import {
  Avatar,
  Box,
  Button,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
} from "@mui/material";
import DashboardLayout from "layout";
import userImage from "../../assets/users.svg";
import flycoin from "../../assets/flycoin.svg";
import withdrawalsImage from "../../assets/withdrawals.svg";
import { ChevronLeft } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import BlockIcon from "@mui/icons-material/Block";
import { useNavigate, useParams } from "react-router-dom";
import checkVerified from "../../assets/check-verified.svg";
import StatCard from "./modules/StatCard";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import UserTable from "./modules/UsersTable";
import WalletHistory from "./modules/WalletHistory";
import Withdrawals from "./modules/Withdrawals";
import {
  getAffiliateUser,
  IAffiliateUserResponse,
  IReferralDetail,
  IWithdrawalDetail,
} from "../../services/affiliate";
import { formatNumberShort } from "utils/helpers";

const AffiliateUserDetails = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const [tabIndex, setTabIndex] = useState(0);
  // React Query (not a mount effect) so revisits reuse the cached user.
  const { data, isLoading: loading } = useQuery({
    queryKey: ["affiliateUser", userId],
    queryFn: () => getAffiliateUser(userId!),
    enabled: Boolean(userId),
  });
  const userData: IAffiliateUserResponse | null = data ?? null;
  const referrals: IReferralDetail[] = data?.data?.referrals || [];
  const withdrawals: IWithdrawalDetail[] = data?.data?.affliateWithdrawal || [];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Format phone number
  const formatPhoneNumber = (phone: number) => {
    const phoneStr = phone.toString();
    return phoneStr.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
  };

  const stringToColor = (value: string) => {
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
      hash = value.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i += 1) {
      const channel = (hash >> (i * 8)) & 0xff;
      color += channel.toString(16).padStart(2, "0");
    }
    return color;
  };

  const calculateTotalEarnings = () => {
    if (!userData) return 0;
    return userData.data.affliate.referral_bonus_balance;
  };

  const calculateWithdrawnAmount = () => {
    if (!withdrawals.length) return 0;
    return withdrawals
      .filter((withdrawal) => withdrawal.status === "success")
      .reduce((total, withdrawal) => total + withdrawal.amount, 0);
  };

  const statCards = userData
    ? [
        {
          imageSrc: userImage,
          label: "Total Referrals",
          value: userData.data.affliate.referral_count.toString(),
        },
        {
          imageSrc: flycoin,
          label: "Total Flycoin Earned",
          value: formatNumberShort(calculateTotalEarnings()),
          subLabel: "NGN",
        },
        {
          imageSrc: withdrawalsImage,
          label: "Withdrawn",
          value: formatNumberShort(calculateWithdrawnAmount()),
          subLabel: "NGN",
        },
      ]
    : [];

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ bgColor: "white", mt: "-38px", mx: -4 }}>
        <Button
          startIcon={<ChevronLeft />}
          onClick={() => navigate(-1)}
          sx={{
            color: "#667185",
            textTransform: "none",
            py: "12px",
            borderTop: "1px solid #E4E7EC",
            borderBottom: "1px solid #E4E7EC",
            width: "100%",
            justifyContent: "flex-start",
            pl: 5,
          }}
        >
          Back
        </Button>
      </Box>
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography fontSize="24px" fontWeight="600" variant="h5">
          Affiliate User Details
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          width={{ xs: "100%", sm: "auto" }}
          paddingTop="10px"
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "6px 16px",
              color: "#344054",
              borderColor: "#344054",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Export CSV
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<BlockIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "6px 16px",
              bgColor: "#DA4733",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Blacklist User
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : userData ? (
        <Box
          sx={{
            px: { xs: 1.5, sm: 3 },
            py: 3,
            bgcolor: "white",
            borderRadius: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "12px", sm: "24px" },
            }}
          >
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Avatar
                alt={`${userData.data.affliate.firstName} ${userData.data.affliate.lastName}`}
                src={userData.data.affliate.image || undefined}
                sx={{
                  width: { xs: 70, sm: 120 },
                  height: { xs: 70, sm: 120 },
                  fontSize: { xs: "24px", sm: "40px" },
                  fontWeight: 600,
                  bgcolor: stringToColor(
                    `${userData.data.affliate.firstName} ${userData.data.affliate.lastName}`,
                  ),
                }}
              >
                {getInitials(
                  userData.data.affliate.firstName,
                  userData.data.affliate.lastName,
                )}
              </Avatar>
              <Box
                component="img"
                alt=""
                src={checkVerified}
                sx={{
                  position: "absolute",
                  zIndex: 11,
                  bottom: "4px",
                  right: "4px",
                  width: { xs: 16, sm: 32 },
                  height: { xs: 16, sm: 32 },
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "14px", sm: "16px" },
                  color: "#344054",
                  fontWeight: 600,
                  mb: 0,
                }}
              >
                {`${userData.data.affliate.firstName} ${userData.data.affliate.lastName}`}
              </Typography>

              <Typography
                sx={{ color: "#475367", fontSize: { xs: "12px", sm: "14px" } }}
                variant="body2"
              >
                Joined on{" "}
                {new Date(
                  userData.data.affliate.createdAt,
                ).toLocaleDateString()}
              </Typography>

              <Typography
                sx={{ color: "#475367", fontSize: { xs: "12px", sm: "14px" } }}
                variant="body2"
              >
                {userData.data.affliate.email}
              </Typography>

              <Typography
                sx={{ color: "#475367", fontSize: { xs: "12px", sm: "14px" } }}
                variant="body2"
              >
                {formatPhoneNumber(userData.data.affliate.phoneNumber)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {statCards.map((stat, index) => (
              <StatCard
                key={index}
                imageSrc={stat?.imageSrc}
                label={stat?.label}
                value={stat?.value}
                subLabel={stat?.subLabel}
              />
            ))}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ width: "100%" }}>
            <Tabs
              value={tabIndex}
              onChange={handleChange}
              aria-label="wallet tabs"
              TabIndicatorProps={{
                sx: { backgroundColor: "#28a745", height: 2 },
              }}
              textColor="inherit"
            >
              <Tab
                label="Referral Logs"
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: tabIndex === 0 ? "#28a745" : "#667185",
                }}
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Wallet History
                  </Box>
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: tabIndex === 1 ? "#28a745" : "#667185",
                }}
              />
              <Tab
                label="Withdrawals"
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: tabIndex === 2 ? "#379E66" : "#667185",
                }}
              />
            </Tabs>

            <TabPanel value={tabIndex} index={0}>
              <UserTable referrals={referrals} />
            </TabPanel>
            <TabPanel value={tabIndex} index={1}>
              <WalletHistory referrals={referrals} />
            </TabPanel>
            <TabPanel value={tabIndex} index={2}>
              <Withdrawals
                withdrawals={withdrawals}
                username={userData.data.affliate.username}
                userId={userData.data.affliate._id}
              />
            </TabPanel>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            User not found or error loading user data
          </Typography>
        </Box>
      )}
    </DashboardLayout>
  );
};

export default AffiliateUserDetails;

interface ITabpanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel = ({ children, value, index }: ITabpanelProps) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};
